import axiosBase from "axios";
import { API_URL, axios } from "./axiosSettings";
import { getAuthHeader } from "../helpers/session";
import { convertStreamToJson } from "../util/buffer";
import { FETCH_CANCEL_MESSAGE } from "../constants/constants";
import { logger } from "./logger";

let channelModCancelToken, channelThresholdToken, channelGroupToken, trendDataByChannelNosToken;
const channelChartToken = {};
const channelConfigToken = {};
const channelNamesToken = {};

export const fetchChannelAllData = async (vesselId) => {
  try {
    const { data } = await axios.get(`/api/channel/alldata`, {
      params: {
        vesselId,
      },
      headers: getAuthHeader(),
    });
    return { channelData: data };
  } catch (e) {
    throw { fetchChannelAllData: e };
  }
};

export const fetchChannelLatestData = async (vesselId) => {
  try {
    const configReq = axios.get(`/api/channelconfig/alldata`, {
      params: {
        vesselId,
      },
      headers: getAuthHeader(),
    });

    const channelReq = axios.get(`/api/channel/latestdata`, {
      params: {
        vesselId,
      },
      headers: getAuthHeader(),
    });

    const [configData, channelData] = await Promise.all([configReq, channelReq]);
    return {
      channelConfigData: configData.data,
      channelLatestData: channelData.data,
    };
  } catch (e) {
    throw { fetchChannelLatestData: e };
  }
};

export const fetchChannelModAllData = async (vesselId) => {
  if(!vesselId) return;
  if (typeof channelModCancelToken !== typeof undefined) {
    channelModCancelToken.abort(FETCH_CANCEL_MESSAGE);
  }

  channelModCancelToken = new AbortController();

  try {
    const response = await fetch(`${API_URL}/api/channelmod/alldata?vesselId=${vesselId}`, {
      method: "GET",
      headers: getAuthHeader(),
      signal: channelModCancelToken.signal,
      responseType: "stream",
    });
    const data = await convertStreamToJson(response.body);

    return { channelData: data };
  } catch (e) {
    throw { fetchChannelModAllData: e };
  }
};

export const fetchChannelNames = async (vesselId, chart) => {
  if (typeof channelNamesToken[chart] !== typeof undefined) {
    channelNamesToken[chart].cancel(FETCH_CANCEL_MESSAGE);
  }

  channelNamesToken[chart] = axiosBase.CancelToken.source();

  try {
    const { data } = await axios.get(`/api/channelmod/names`, {
      params: {
        vesselId,
      },
      headers: getAuthHeader(),
      cancelToken: channelNamesToken[chart].token,
    });
    return { names: data };
  } catch (e) {
    throw { fetchChannelNames: e };
  }
};

export const fetchChannelConfig = async(vesselId, chartId) => {
  if(!vesselId) return;
  if (typeof channelConfigToken[chartId] !== typeof undefined) {
    channelConfigToken[chartId].cancel(FETCH_CANCEL_MESSAGE);
  }

  channelConfigToken[chartId] = axiosBase.CancelToken.source();

  try {
    const { data } = await axios.get(`/api/channelmod/config`, {
      params: {
        vesselId,
        chartId,
      },
      headers: getAuthHeader(),
      cancelToken: channelConfigToken[chartId].token,
    });
    return { config: data };
  } catch (e) {
    throw { fetchChannelconfig: e };
  }
};

export const fetchChannelDataByChartId = async (
  vesselId,
  chartId = '',
  startDate,
  endDate,
  interval,
  limit = '',
  category = '',
  subcategory = '',
  secretkey = 'default',
  signal = null,
) => {
  const key = !subcategory || subcategory === "" ? secretkey : subcategory;
  if (typeof channelChartToken[key] !== typeof undefined) {
    channelChartToken[key].abort(FETCH_CANCEL_MESSAGE);
  }

  channelChartToken[key] = new AbortController();

  const url = `${API_URL}/api/channelmod/trenddata?vesselId=${vesselId}`
    + `&chartId=${chartId ?? ''}&startDate=${startDate}&endDate=${endDate}&minuteInterval=${interval}`
    + `&limit=${limit}&category=${category}&subcategory=${subcategory}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeader(),
      signal: signal ?? channelChartToken[key].signal,
      responseType: "stream",
    });
    const data = await convertStreamToJson(response.body);
    return { customData: data };
  } catch (e) {
    throw { fetchChannelTrend: e };
  }
};

export const getChannelThresholdData = async (vesselId, channelNo) => {
  if (typeof channelThresholdToken !== typeof undefined) {
    channelThresholdToken.cancel(FETCH_CANCEL_MESSAGE);
  }

  channelThresholdToken = axiosBase.CancelToken.source();

  try {
    const { data } = await axios.get(`/api/channelmod/threshold`, {
      params: {
        vesselId,
        channelNo,
      },
      headers: getAuthHeader(),
      cancelToken: channelThresholdToken.token,
    });
    return { channelThresholdData: data };
  } catch (e) {
    throw { getChannelThresholdData: e };
  }
};

export const postChannelThresholdData = async (vesselId, thresholdSettings) => {
  if(!vesselId) return;
  if (typeof channelThresholdToken !== typeof undefined) {
    channelThresholdToken.cancel(FETCH_CANCEL_MESSAGE);
  }

  channelThresholdToken = axiosBase.CancelToken.source();
  try {
    const { data } = await axios.post(`/api/channelmod/threshold/${vesselId}`, thresholdSettings, {
      params: {
        shortUuid: vesselId,
      },
      headers: getAuthHeader(),
      cancelToken: channelThresholdToken.token,
    });
    return { channelThresholdData: data };
  } catch (e) {
    throw { postChannelThresholdData: e };
  }
};

// for channel group
export const getChannelGroup = async (vesselId) => {
  if(!vesselId) return;
  if (typeof channelGroupToken !== typeof undefined) {
    channelGroupToken.cancel(FETCH_CANCEL_MESSAGE);
  }

  channelGroupToken = axiosBase.CancelToken.source();
  try {
    const { data } = await axios.get(`/api/channelmod/group`, {
      params: {
        vesselId
      },
      headers: getAuthHeader(),
      cancelToken: channelGroupToken.token,
    });
    if(!data || data?.length === 0) return {channelGroupData: []};
    const formattedData = data.map((e) => {return ({...e, values: e.values.filter((v) => v !== '')})});
    return { channelGroupData: formattedData };
  } catch (e) {
    throw { getChannelGroupData: e };
  }
}

export const createChannelGroup = async (vesselId, newGroupParams) => {
  if(!vesselId) return;
  if (typeof channelGroupToken !== typeof undefined) {
    channelGroupToken.cancel(FETCH_CANCEL_MESSAGE);
  }

  channelGroupToken = axiosBase.CancelToken.source();
  try {
    const { data } = await axios.post(`/api/channelmod/group`, 
      {
        vesselId,
        ...newGroupParams,
      },
      {
        headers: getAuthHeader(),
        cancelToken: channelGroupToken.token,
      }
    );
    return { channelGroupData: data };
  } catch (e) {
    throw { postChannelGroupData: e };
  }
}

export const updateChannelGroup = async (vesselId, groupId, newGroupParams) => {
  if(!vesselId) return;
  if (typeof channelGroupToken !== typeof undefined) {
    channelGroupToken.cancel(FETCH_CANCEL_MESSAGE);
  }
  channelGroupToken = axiosBase.CancelToken.source();
  try {
    const { data } = await axios.post(`/api/channelmod/group`,
      {
        vesselId,
        id: groupId,
        name: newGroupParams.name,
        values: newGroupParams.values,
      },
      {
        headers: getAuthHeader(),
        cancelToken: channelGroupToken.token,
      }
    );
    return { channelGroupData: data };
  } catch (e) {
    throw { postChannelGroupData: e };
  }
}

export const deleteChannelGroup = async (vesselId, groupId) => {
  if(!vesselId || !groupId) return;
  if (typeof channelGroupToken !== typeof undefined) {
    channelGroupToken.cancel(FETCH_CANCEL_MESSAGE);
  }
  channelGroupToken = axiosBase.CancelToken.source();

  try {
    const { data } = await axios.delete(`/api/channelmod/group`, {
      params: {
        vesselId,
        id: groupId,
      },
      headers: getAuthHeader(),
      cancelToken: channelGroupToken.token,
    });
    return { channelGroupData: data };
  } catch (e) {
    throw { postChannelGroupData: e };
  }
}

// for sep new trend
export const fetchTrendDataByChannelNos = async (
  vesselId,
  chNos,
  startDate,
  endDate,
  interval,
  signal = null,
) => {
  if (typeof trendDataByChannelNosToken !== typeof undefined) {
    trendDataByChannelNosToken.abort(FETCH_CANCEL_MESSAGE);
  }

  trendDataByChannelNosToken = new AbortController();
  const url = `${API_URL}/api/channel/live?vesselId=${vesselId}`
    + `&chNos=${chNos ?? ''}&startDate=${startDate}&endDate=${endDate}&minuteInterval=${interval}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeader(),
      signal,
      responseType: "stream",
    });
    const data = await convertStreamToJson(response.body);
    return { trendData: data };
  } catch (e) {
    logger.error(JSON.stringify(e));
  }
};